/**
 * Verification utilities for template and generator creators.
 * Helps ensure proper design system integration.
 */

import { ThemeProvider } from '../theme/ThemeProvider';
import { useTheme } from '../theme/ThemeProvider';
import { cn } from './cn';
import { Button } from '../components/Button/Button';
import { LoadingButton } from '../components/Loading/LoadingButton';
import { LoadingContainer } from '../components/Loading/LoadingContainer';
import { LoadingSkeleton } from '../components/Loading/LoadingSkeleton';
import { useAsyncOperation } from '../hooks/useAsyncOperation';
import type { Theme } from '../theme/types';

export interface DesignSystemExports {
  // Core utilities
  cn?: typeof cn;

  // Theme system
  ThemeProvider?: typeof ThemeProvider;
  useTheme?: typeof useTheme;
  Theme?: unknown; // Type export

  // Components
  Button?: typeof Button;
  LoadingButton?: typeof LoadingButton;
  LoadingContainer?: typeof LoadingContainer;
  LoadingSkeleton?: typeof LoadingSkeleton;

  // Hooks
  useAsyncOperation?: typeof useAsyncOperation;
}

/**
 * Verifies that all design system imports are available.
 * Useful for template creators to ensure proper setup.
 *
 * @example
 * // In your template's setup file
 * import { verifyDesignSystemImports } from '@orchard9ai/design-system';
 *
 * // Check all imports are available
 * const imports = verifyDesignSystemImports();
 * console.log('Design system ready:', imports.allPresent);
 *
 * @returns Object with import status and any missing imports
 */
export function verifyDesignSystemImports(): {
  exports: Record<string, unknown>;
  missing: string[];
  allPresent: boolean;
} {
  const exports: Record<string, unknown> = {
    // Core utilities
    cn: typeof cn !== 'undefined' ? cn : undefined,

    // Theme system
    ThemeProvider: typeof ThemeProvider !== 'undefined' ? ThemeProvider : undefined,
    useTheme: typeof useTheme !== 'undefined' ? useTheme : undefined,
    Theme: true, // Type exports always available

    // Components
    Button: typeof Button !== 'undefined' ? Button : undefined,
    LoadingButton: typeof LoadingButton !== 'undefined' ? LoadingButton : undefined,
    LoadingContainer: typeof LoadingContainer !== 'undefined' ? LoadingContainer : undefined,
    LoadingSkeleton: typeof LoadingSkeleton !== 'undefined' ? LoadingSkeleton : undefined,

    // Hooks
    useAsyncOperation: typeof useAsyncOperation !== 'undefined' ? useAsyncOperation : undefined,
  };

  const missing = Object.entries(exports)
    .filter(([_, value]) => !value)
    .map(([key]) => key);

  return {
    exports,
    missing,
    allPresent: missing.length === 0,
  };
}

/**
 * Validates theme configuration for templates.
 *
 * @example
 * // Validate your template's theme setup
 * const validation = validateThemeConfiguration({
 *   defaultTheme: 'grove-light',
 *   supportedThemes: ['grove-light', 'grove-dark'],
 * });
 *
 * if (!validation.valid) {
 *   console.error('Theme configuration errors:', validation.errors);
 * }
 *
 * @param config - Theme configuration to validate
 * @returns Validation result with any errors
 */
export function validateThemeConfiguration(config: {
  defaultTheme?: string;
  supportedThemes?: string[];
}): {
  valid: boolean;
  errors: string[];
} {
  const errors: string[] = [];
  const validThemes = ['light', 'dark', 'cupcake', 'business', 'grove-light', 'grove-dark'];

  if (config.defaultTheme && !validThemes.includes(config.defaultTheme)) {
    errors.push(
      `Invalid defaultTheme "${config.defaultTheme}". Valid themes: ${validThemes.join(', ')}`
    );
  }

  if (config.supportedThemes) {
    const invalidThemes = config.supportedThemes.filter((theme) => !validThemes.includes(theme));
    if (invalidThemes.length > 0) {
      errors.push(
        `Invalid themes: ${invalidThemes.join(', ')}. Valid themes: ${validThemes.join(', ')}`
      );
    }
  }

  return {
    valid: errors.length === 0,
    errors,
  };
}

/**
 * Helper to generate template metadata for design system integration.
 *
 * @example
 * // In your template's package.json or config
 * const metadata = generateTemplateMetadata({
 *   templateName: 'vite-vike-tauri',
 *   defaultTheme: 'grove-light',
 *   requiredImports: ['ThemeProvider', 'cn'],
 * });
 *
 * @param options - Template configuration options
 * @returns Template metadata object
 */
export function generateTemplateMetadata(options: {
  templateName: string;
  designSystemVersion?: string;
  defaultTheme?: Theme;
  requiredImports?: (keyof DesignSystemExports)[];
  optionalImports?: (keyof DesignSystemExports)[];
}) {
  return {
    name: options.templateName,
    designSystem: {
      version: options.designSystemVersion || '0.1.2',
      defaultTheme: options.defaultTheme || 'light',
      requiredImports: options.requiredImports || ['ThemeProvider', 'cn'],
      optionalImports: options.optionalImports || ['useTheme'],
      configuration: {
        cssImport: '@orchard9ai/design-system/dist/styles.css',
        tailwindPreset: '@orchard9ai/design-system/tailwind.config.js',
      },
    },
    generated: new Date().toISOString(),
  };
}
